/** @file This file contains the loading code for the websocket client. */
import moment from 'moment';
import { EitherAsync, identity } from 'purify-ts';
import { Maybe } from '@codegrade/maybe';
import * as utils from '@codegrade/utils';
import { UserStore } from './store';
import { coerceToString, discard, signal } from './utils';
const defaultClientLocation = `${IS_PRODUCTION ? 'wss' : 'ws'}://${window.location.host}/api/ws/v1`;
let wsClientLocation = defaultClientLocation;
if (!IS_PRODUCTION && WEBSOCKET_LOCATION != null && WEBSOCKET_LOCATION !== '') {
    wsClientLocation = WEBSOCKET_LOCATION;
}
const DEFAULT_OPTS = {
    timeout: moment.duration(30, 'seconds'),
};
/** The error used when loading the websocket failed. */
export class WebsocketLoadError extends Error {
    /** The tag of this class. */
    tag = '$WebsocketLoadError$';
    /** @param message The message of this error. */
    constructor(message) {
        super(message);
        this.name = this.constructor.name;
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
let augmentWsClientModule = (mod) => {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    class TimeoutFailure extends mod.SendFailure {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        constructor() {
            super('TimeoutFailure: The websocket connection timed-out', {
                message: 'The websocket connection timed-out',
            });
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    class WsClient extends mod.Client {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        getJwt() {
            return UserStore.dangerousSession().map(({ accessToken }) => accessToken);
        }
    }
    if (!IS_PRODUCTION) {
        // @ts-expect-error: Typescript doesn't allow us to add stuff to
        // window.
        window.WebsocketClient = WsClient;
    }
    const client = new WsClient(wsClientLocation);
    signal.AFTER_USER_CHANGED.register(async (newUser) => {
        client.reset();
        if (newUser.isJust()) {
            client.open();
        }
        else {
            client.close();
        }
    });
    const res = {
        client,
        TimeoutFailure,
        ...mod,
    };
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    augmentWsClientModule = () => res;
    return res;
};
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function innerGetClient() {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    return EitherAsync(async ({ throwE }) => {
        let mod;
        try {
            mod = await import('@codegrade/ws-client');
        }
        catch (error) {
            return throwE(new WebsocketLoadError(coerceToString(error)));
        }
        return augmentWsClientModule(mod);
    });
}
let connectingPromise = Maybe.empty();
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export default function getWsClient(opts = DEFAULT_OPTS) {
    return EitherAsync(async (helpers) => {
        const { client, TimeoutFailure, WS_CONNECTED } = await helpers.fromPromise(innerGetClient());
        if (client.isOpen) {
            return client;
        }
        let deregister = discard;
        const connectPromise = connectingPromise.caseOf({
            Nothing: () => {
                const promise = new Promise(resolve => {
                    deregister = WS_CONNECTED.register(async () => {
                        resolve(client);
                    });
                    client.open();
                }).finally(() => {
                    connectingPromise = Maybe.empty();
                });
                connectingPromise = Maybe.of(promise);
                return promise;
            },
            Just: identity,
        });
        let connected;
        try {
            connected = await utils.promise.timeout(connectPromise, opts.timeout);
        }
        finally {
            deregister();
        }
        return connected.throwOnEmpty(new TimeoutFailure());
    });
}
if (!IS_PRODUCTION) {
    // @ts-expect-error: Typescript doesn't allow us to add stuff to
    // window.
    window.websocketEndpoint = wsClientLocation;
    // @ts-expect-error: Typescript doesn't allow us to add stuff to
    // window.
    window.getWsClient = getWsClient;
}
